import { ErrorBoundary as ReactErrorBoundary } from "react-error-boundary";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ErrorBoundary as SentryErrorBoundary } from "@sentry/react";

import { IS_LOCAL } from "@/constants/app.constants";
import { ROUTES } from "@/constants/routes.constants";
import { medplumClient } from "@/lib/medplum";

import { Banner, Button, Card, Heading, Text } from "@/components/base";

/**
 * Sentry v8 widened the fallback's `error` from `Error` to `unknown`. This
 * Banner shows the raw error detail rather than UI copy — the same raw
 * `message` it always showed — so a non-`Error` throw falls back to its own
 * string form rather than a translated stand-in.
 */
const toErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

type FallbackProps = {
  errorMessage: string;
  eventId?: string | null;
  onReset?: () => void;
};

const ErrorFallback: React.FC<FallbackProps> = ({
  errorMessage,
  eventId,
  onReset = () => window.location.reload(),
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <Card padding="lg" className="w-full max-w-md">
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-1">
            <Heading as="h1" variant="headingLg">
              {t("Something went wrong")}
            </Heading>
            <Text variant="bodyMd" tone="subdued">
              {t("An unexpected error occurred. Please try again.")}
            </Text>
          </div>
          <Banner tone="critical" title={t("Error details")}>
            {errorMessage}
          </Banner>
          {eventId && (
            <Text variant="bodySm" tone="subdued">
              {t("Event ID: {{id}}", { id: eventId })}
            </Text>
          )}
          <div className="flex justify-end gap-2">
            <Button
              variant="critical"
              onClick={() => {
                void medplumClient
                  .signOut()
                  .finally(() => window.location.assign(ROUTES.LOGIN));
              }}
            >
              {t("Log out")}
            </Button>
            <Button variant="secondary" onClick={onReset}>
              {t("Try again")}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

type Props = {
  children: React.ReactNode;
  onReset?: () => void;
};

export const ErrorBoundary: React.FC<Props> = ({ children, onReset }) => {
  const { t } = useTranslation();

  if (IS_LOCAL) {
    return (
      <ReactErrorBoundary
        fallbackRender={({ error }) => (
          <ErrorFallback
            errorMessage={toErrorMessage(error)}
            onReset={onReset}
          />
        )}
      >
        {children}
      </ReactErrorBoundary>
    );
  }

  return (
    <SentryErrorBoundary
      onError={() => toast.error(t("Something went wrong!"))}
      // eslint-disable-next-line @typescript-eslint/unbound-method
      fallback={({ error, eventId }) => (
        <ErrorFallback
          errorMessage={toErrorMessage(error)}
          eventId={eventId}
          onReset={onReset}
        />
      )}
    >
      {children}
    </SentryErrorBoundary>
  );
};
